import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  Clock,
  Database,
  Download,
  Edit,
  RotateCcw,
  ThumbsUp,
  SkipForward,
  ChevronDown,
  ChevronRight,
  History,
  MessageSquare,
  Save,
  Loader2,
  Lock,
  Eye,
  Paperclip,
  Image as ImageIcon,
  FileIcon,
} from 'lucide-react';
import { Badge } from '@/components/common/Badge';
import { Button } from '@/components/common/Button';
import { Textarea } from '@/components/common/Textarea';
import { Modal } from '@/components/common/Modal';
import { Spinner } from '@/components/common/Spinner';
import { taskPipelineService, ConversationAttachmentSummary } from '@/services/taskPipeline.service';
import { CustomerTaskProgress, TaskDocumentRevision } from '@/types';
import toast from 'react-hot-toast';
import { cn } from '@/utils/cn';

interface TaskProgressPanelProps {
  conversationId: string;
}

export const TaskProgressPanel: React.FC<TaskProgressPanelProps> = ({ conversationId }) => {
  const [progressList, setProgressList] = useState<CustomerTaskProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [hasData, setHasData] = useState(false);

  // Document edit/view state
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Full document view modal
  const [viewingDoc, setViewingDoc] = useState<CustomerTaskProgress | null>(null);

  // Revision modal
  const [revisionModal, setRevisionModal] = useState<{ id: string; mode: 'ai_regenerate' | 'reach_customer' } | null>(null);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [isSubmittingRevision, setIsSubmittingRevision] = useState(false);

  // Revision history modal
  const [revisionHistoryModal, setRevisionHistoryModal] = useState<string | null>(null);
  const [revisions, setRevisions] = useState<TaskDocumentRevision[]>([]);
  const [isLoadingRevisions, setIsLoadingRevisions] = useState(false);

  // Downloading
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  // Attachments
  const [attachments, setAttachments] = useState<ConversationAttachmentSummary[]>([]);

  const loadProgress = useCallback(async () => {
    try {
      const [data, atts] = await Promise.all([
        taskPipelineService.getProgressForConversation(conversationId),
        taskPipelineService.getAttachmentsForConversation(conversationId).catch(() => []),
      ]);
      setProgressList(data);
      setAttachments(atts);
      setHasData(data.length > 0);
    } catch (error) {
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Auto-refresh every 10s while panel is open
  useEffect(() => {
    if (!hasData) return;
    const interval = setInterval(loadProgress, 10000);
    return () => clearInterval(interval);
  }, [hasData, loadProgress]);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'completed':
        return { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100', border: 'border-green-200', label: 'Completed', pill: 'bg-green-100 text-green-700' };
      case 'in_progress':
        return { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', border: 'border-blue-200', label: 'In Progress', pill: 'bg-blue-100 text-blue-700' };
      case 'blocked':
        return { icon: Lock, color: 'text-orange-600', bg: 'bg-orange-100', border: 'border-orange-200', label: 'Blocked', pill: 'bg-orange-100 text-orange-700' };
      case 'skipped':
        return { icon: SkipForward, color: 'text-gray-500', bg: 'bg-gray-100', border: 'border-[#1a1a2e]', label: 'Skipped', pill: 'bg-gray-100 text-gray-600' };
      default:
        return { icon: Clock, color: 'text-gray-400', bg: 'bg-gray-50', border: 'border-[#1a1a2e]', label: 'Pending', pill: 'bg-gray-100 text-gray-500' };
    }
  };

  const handleMarkComplete = async (progressId: string) => {
    try {
      await taskPipelineService.markTaskComplete(progressId);
      toast.success('Task marked as complete');
      await loadProgress();
    } catch (error) {
      toast.error('Failed to complete task');
    }
  };

  const handleSkip = async (progressId: string) => {
    if (!confirm('Skip this task? It can be resumed later.')) return;
    try {
      await taskPipelineService.markTaskSkipped(progressId);
      toast.success('Task skipped');
      await loadProgress();
    } catch (error) {
      toast.error('Failed to skip task');
    }
  };

  const handleApprove = async (progressId: string) => {
    try {
      await taskPipelineService.approveDocument(progressId);
      toast.success('Document approved');
      await loadProgress();
    } catch (error) {
      toast.error('Failed to approve document');
    }
  };

  const handleStartEdit = (progress: CustomerTaskProgress) => {
    setEditingDocId(progress.id);
    setEditContent(progress.generated_content || '');
  };

  const handleSaveEdit = async () => {
    if (!editingDocId) return;
    setIsSavingEdit(true);
    try {
      await taskPipelineService.editDocument(editingDocId, editContent);
      toast.success('Document updated');
      setEditingDocId(null);
      await loadProgress();
    } catch (error) {
      toast.error('Failed to save edit');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleRegenerate = async (progressId: string) => {
    if (!confirm('Regenerate this document with AI?')) return;
    try {
      await taskPipelineService.regenerateDocument(progressId);
      toast.success('Document regenerated');
      await loadProgress();
    } catch (error) {
      toast.error('Failed to regenerate');
    }
  };

  const handleSubmitRevision = async () => {
    if (!revisionModal || !revisionFeedback.trim()) {
      toast.error('Please provide feedback');
      return;
    }
    setIsSubmittingRevision(true);
    try {
      await taskPipelineService.requestRevision(revisionModal.id, revisionFeedback, revisionModal.mode);
      toast.success(
        revisionModal.mode === 'ai_regenerate'
          ? 'AI is regenerating with your feedback'
          : 'Reaching back to customer for more info'
      );
      setRevisionModal(null);
      setRevisionFeedback('');
      await loadProgress();
    } catch (error) {
      toast.error('Failed to request revision');
    } finally {
      setIsSubmittingRevision(false);
    }
  };

  const handleViewRevisions = async (progressId: string) => {
    setRevisionHistoryModal(progressId);
    setIsLoadingRevisions(true);
    try {
      const data = await taskPipelineService.getRevisionHistory(progressId);
      setRevisions(data);
    } catch (error) {
      toast.error('Failed to load revision history');
    } finally {
      setIsLoadingRevisions(false);
    }
  };

  const handleDownload = async (progressId: string, format: 'docx' | 'pdf', taskName?: string) => {
    setDownloadingId(progressId);
    try {
      const blob = await taskPipelineService.downloadDocument(progressId, format);
      const url = URL.createObjectURL(blob);
      const safeName = (taskName || 'document').replace(/[^a-zA-Z0-9_-]/g, '_');

      if (format === 'pdf') {
        // Backend returns HTML — open in new tab for print-to-PDF
        const win = window.open(url, '_blank');
        if (win) {
          // Auto-trigger print dialog after content loads
          win.onload = () => win.print();
        }
        // Delay revocation to keep the tab working
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        // DOCX — download as file
        const a = document.createElement('a');
        a.href = url;
        a.download = `${safeName}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      }
    } catch (error) {
      toast.error(`Failed to download ${format.toUpperCase()}`);
    } finally {
      setDownloadingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="sm" />
      </div>
    );
  }

  if (!hasData) return (
    <div className="flex flex-col items-center justify-center py-8 text-gray-400">
      <Clock className="w-8 h-8 mb-2 text-gray-300" />
      <p className="text-xs">No tasks yet</p>
    </div>
  );

  const completedCount = progressList.filter(p => p.status === 'completed').length;
  const totalCount = progressList.length;
  const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="px-5 py-4 border-b-2 border-[#1a1a2e] bg-gray-50 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Database className="h-4 w-4 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-sm">Task Pipeline</h3>
              <p className="text-xs text-gray-500">{completedCount} of {totalCount} tasks done</p>
            </div>
          </div>
          <span className="text-lg font-bold text-gray-900">{progressPercent}%</span>
        </div>
        <div className="w-full h-2.5 bg-gray-200 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all duration-500',
              progressPercent === 100 ? 'bg-green-500' : 'bg-blue-500'
            )}
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Customer Files Section */}
      {attachments.length > 0 && (
        <div className="mx-3 mt-2 mb-1 bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
          <div className="px-3 py-2 bg-purple-50 border-b border-purple-200 flex items-center gap-2">
            <Paperclip className="w-3.5 h-3.5 text-purple-600" />
            <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">
              Customer Files ({attachments.length})
            </p>
          </div>
          <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
            {attachments.map((att) => (
              <div key={att.id} className="flex items-center gap-2 px-3 py-2">
                {att.mime_type.startsWith('image/') ? (
                  <ImageIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                ) : (
                  <FileIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 truncate">{att.filename}</p>
                  <p className="text-xs text-gray-400">
                    {(att.file_size / 1024).toFixed(1)} KB
                    {att.field_name && <span className="ml-1 text-purple-500">→ {att.field_name}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {att.mime_type.startsWith('image/') && (
                    <button
                      onClick={async () => {
                        try {
                          const full = await taskPipelineService.getAttachment(att.id);
                          const byteChars = atob(full.content_base64);
                          const bytes = new Uint8Array(byteChars.length);
                          for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
                          const blob = new Blob([bytes], { type: full.mime_type });
                          window.open(URL.createObjectURL(blob), '_blank');
                        } catch { toast.error('Failed to view file'); }
                      }}
                      className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                      title="View"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={async () => {
                      try {
                        const full = await taskPipelineService.getAttachment(att.id);
                        const byteChars = atob(full.content_base64);
                        const bytes = new Uint8Array(byteChars.length);
                        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
                        const blob = new Blob([bytes], { type: full.mime_type });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = att.filename;
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                        toast.success('Download started');
                      } catch { toast.error('Failed to download file'); }
                    }}
                    className="p-1 text-gray-500 hover:text-purple-700 hover:bg-purple-50 rounded transition-colors"
                    title="Download"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Task List */}
      <div className="flex-1 overflow-y-auto">
        {progressList.map((progress) => {
          const statusConfig = getStatusConfig(progress.status);
          const StatusIcon = statusConfig.icon;
          const isExpanded = expandedId === progress.id;
          const isDocTask = progress.task_type === 'document_creation';
          const isEditing = editingDocId === progress.id;
          const collectedEntries = Object.entries(progress.collected_data || {});

          return (
            <div key={progress.id} className={cn(
              'border-b-2 border-[#1a1a2e]',
              isExpanded && 'bg-gray-50/50'
            )}>
              {/* Task Row */}
              <div
                className="flex items-center gap-3 px-5 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : progress.id)}
              >
                <div className={cn('flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center', statusConfig.bg)}>
                  <StatusIcon className={cn('h-4 w-4', statusConfig.color)} />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    {progress.task_name || 'Task'}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant={isDocTask ? 'success' : 'info'} className="text-[10px] px-1.5 py-0">
                      {isDocTask ? 'Document' : 'Data'}
                    </Badge>
                    {isDocTask && progress.generated_content && (
                      <span className="text-[10px] text-gray-400">
                        {progress.generated_content.length} chars
                      </span>
                    )}
                    {!isDocTask && collectedEntries.length > 0 && (
                      <span className="text-[10px] text-gray-400">
                        {collectedEntries.length} field{collectedEntries.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>

                <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full', statusConfig.pill)}>
                  {statusConfig.label}
                </span>

                <div className="flex-shrink-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-5 pb-4 space-y-3">
                  {/* Collected Data */}
                  {collectedEntries.length > 0 && (
                    <div className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden">
                      <div className="px-3 py-2 bg-gray-50 border-b-2 border-[#1a1a2e]">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Collected Data</p>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {collectedEntries.map(([key, value]) => {
                          const strValue = String(value);
                          const isAttachment = strValue.startsWith('attachment:');
                          const attachId = isAttachment ? strValue.replace('attachment:', '') : null;
                          const att = attachId ? attachments.find(a => a.id === attachId) : null;

                          return (
                            <div key={key} className="px-3 py-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-gray-500 font-medium min-w-0 flex-shrink-0">{key}</span>
                                {!isAttachment && (
                                  <span className="text-xs text-gray-900 font-medium text-right ml-3 truncate max-w-[55%]">
                                    {strValue}
                                  </span>
                                )}
                              </div>
                              {att && (
                                <div className="mt-2 flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                                  {att.mime_type.startsWith('image/') ? (
                                    <ImageIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                  ) : (
                                    <FileIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-700 truncate">{att.filename}</p>
                                    <p className="text-xs text-gray-400">{(att.file_size / 1024).toFixed(1)} KB</p>
                                  </div>
                                  <Paperclip className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                </div>
                              )}
                              {isAttachment && !att && (
                                <span className="text-xs text-gray-400 italic">File attached</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Generated Document */}
                  {isDocTask && progress.generated_content && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Generated Document</p>
                        <div className="flex items-center gap-1">
                          {progress.revision_number > 0 && (
                            <span className="text-xs text-gray-400 mr-1">v{progress.revision_number + 1}</span>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); setViewingDoc(progress); }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View full document"
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewRevisions(progress.id); }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Revision history"
                          >
                            <History className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>

                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="w-full h-64 p-3 text-sm border-2 border-[#1a1a2e] rounded-xl bg-white resize-y focus:ring-2 focus:ring-blue-500 focus:border-transparent leading-relaxed"
                          />
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={handleSaveEdit} isLoading={isSavingEdit}>
                              <Save className="h-3.5 w-3.5 mr-1" />
                              Save
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditingDocId(null)}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="bg-white border-2 border-[#1a1a2e] rounded-xl p-3 max-h-48 overflow-y-auto cursor-pointer hover:border-blue-300 transition-colors"
                          onClick={(e) => { e.stopPropagation(); setViewingDoc(progress); }}
                        >
                          <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
                            {progress.generated_content}
                          </pre>
                        </div>
                      )}

                      {/* Approved badge */}
                      {progress.approved_at && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border-2 border-[#1a1a2e] rounded-xl w-fit">
                          <ThumbsUp className="h-3.5 w-3.5 text-green-600" />
                          <span className="text-xs font-semibold text-green-700">Approved</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                    {/* Data collection actions */}
                    {!isDocTask && progress.status !== 'completed' && progress.status !== 'skipped' && progress.status !== 'blocked' && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => handleMarkComplete(progress.id)}>
                          <CheckCircle className="h-3.5 w-3.5 mr-1.5" />
                          Complete
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleSkip(progress.id)}>
                          <SkipForward className="h-3.5 w-3.5 mr-1.5" />
                          Skip
                        </Button>
                      </>
                    )}

                    {/* Document actions */}
                    {isDocTask && progress.generated_content && !isEditing && (
                      <div className="w-full space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {!progress.approved_at && (
                            <Button size="sm" onClick={() => handleApprove(progress.id)}>
                              <ThumbsUp className="h-3.5 w-3.5 mr-1.5" />
                              Approve
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => handleStartEdit(progress)}>
                            <Edit className="h-3.5 w-3.5 mr-1.5" />
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => handleRegenerate(progress.id)}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                            Regenerate
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRevisionModal({ id: progress.id, mode: 'ai_regenerate' });
                              setRevisionFeedback('');
                            }}
                          >
                            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
                            Revision
                          </Button>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(progress.id, 'docx', progress.task_name)}
                            disabled={downloadingId === progress.id}
                          >
                            {downloadingId === progress.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <Download className="h-3.5 w-3.5 mr-1" />
                                DOCX
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDownload(progress.id, 'pdf', progress.task_name)}
                            disabled={downloadingId === progress.id}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" />
                            Print
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Full Document View Modal */}
      <Modal
        isOpen={!!viewingDoc}
        onClose={() => setViewingDoc(null)}
        size="xl"
      >
        {viewingDoc && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{viewingDoc.task_name}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="success">Document</Badge>
                  {viewingDoc.revision_number > 0 && (
                    <span className="text-sm text-gray-400">Version {viewingDoc.revision_number + 1}</span>
                  )}
                  {viewingDoc.approved_at && (
                    <Badge variant="success">Approved</Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(viewingDoc.id, 'docx', viewingDoc.task_name)}
                  disabled={downloadingId === viewingDoc.id}
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  DOCX
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(viewingDoc.id, 'pdf', viewingDoc.task_name)}
                  disabled={downloadingId === viewingDoc.id}
                >
                  <Download className="h-4 w-4 mr-1.5" />
                  Print
                </Button>
              </div>
            </div>
            <div className="max-h-[65vh] overflow-y-auto border-2 border-[#1a1a2e] rounded-xl bg-white p-6">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed font-sans">
                {viewingDoc.generated_content}
              </pre>
            </div>
            <div className="flex items-center gap-3 mt-4 pt-4 border-t">
              {!viewingDoc.approved_at && (
                <Button onClick={() => { handleApprove(viewingDoc.id); setViewingDoc(null); }}>
                  <ThumbsUp className="h-4 w-4 mr-2" />
                  Approve
                </Button>
              )}
              <Button variant="outline" onClick={() => setViewingDoc(null)}>Close</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Revision Request Modal */}
      <Modal
        isOpen={!!revisionModal}
        onClose={() => setRevisionModal(null)}
        size="lg"
      >
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4">Request Revision</h2>

          <div className="space-y-3 mb-4">
            <label className="block text-sm font-medium text-gray-900">Revision Mode</label>
            <div className="flex gap-3">
              <button
                onClick={() => revisionModal && setRevisionModal({ ...revisionModal, mode: 'ai_regenerate' })}
                className={cn(
                  'flex-1 p-4 rounded-xl border-2 text-left transition-all',
                  revisionModal?.mode === 'ai_regenerate'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-[#1a1a2e] hover:border-gray-300'
                )}
              >
                <RotateCcw className="h-5 w-5 text-blue-600 mb-1.5" />
                <p className="font-medium text-sm">AI Regenerate</p>
                <p className="text-xs text-gray-500 mt-0.5">AI rewrites using your feedback</p>
              </button>
              <button
                onClick={() => revisionModal && setRevisionModal({ ...revisionModal, mode: 'reach_customer' })}
                className={cn(
                  'flex-1 p-4 rounded-xl border-2 text-left transition-all',
                  revisionModal?.mode === 'reach_customer'
                    ? 'border-green-500 bg-green-50'
                    : 'border-[#1a1a2e] hover:border-gray-300'
                )}
              >
                <MessageSquare className="h-5 w-5 text-green-600 mb-1.5" />
                <p className="font-medium text-sm">Reach Customer</p>
                <p className="text-xs text-gray-500 mt-0.5">Ask customer for more info first</p>
              </button>
            </div>
          </div>

          <Textarea
            label="Feedback / Instructions"
            value={revisionFeedback}
            onChange={(e) => setRevisionFeedback(e.target.value)}
            rows={6}
            placeholder={
              revisionModal?.mode === 'ai_regenerate'
                ? 'e.g., Make it more formal. Add more detail about academic achievements.'
                : "e.g., We need the student's GPA and specific research interests."
            }
          />

          <div className="flex items-center gap-3 pt-4 mt-4 border-t">
            <Button onClick={handleSubmitRevision} isLoading={isSubmittingRevision}>
              <Save className="h-4 w-4 mr-2" />
              Submit
            </Button>
            <Button onClick={() => setRevisionModal(null)} variant="outline">Cancel</Button>
          </div>
        </div>
      </Modal>

      {/* Revision History Modal */}
      <Modal
        isOpen={!!revisionHistoryModal}
        onClose={() => setRevisionHistoryModal(null)}
        size="lg"
      >
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4">Revision History</h2>

          {isLoadingRevisions ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="md" />
            </div>
          ) : revisions.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No revision history</p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              {revisions.map((rev) => (
                <div key={rev.id} className="border-2 border-[#1a1a2e] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant={rev.generated_by === 'ai' ? 'info' : 'default'}>
                        {rev.generated_by === 'ai' ? 'AI Generated' : 'Client Edit'}
                      </Badge>
                      <span className="text-sm text-gray-500 font-medium">v{rev.revision_number + 1}</span>
                    </div>
                    <span className="text-sm text-gray-400">
                      {new Date(rev.created_at).toLocaleString()}
                    </span>
                  </div>
                  {rev.feedback && (
                    <div className="bg-amber-50 border-2 border-[#1a1a2e] rounded-xl p-3 mb-3">
                      <p className="text-sm text-amber-800">
                        <span className="font-semibold">Feedback:</span> {rev.feedback}
                      </p>
                    </div>
                  )}
                  <div className="bg-gray-50 rounded-xl p-3 max-h-40 overflow-y-auto border-2 border-[#1a1a2e]">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
                      {rev.content}
                    </pre>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4 mt-4 border-t">
            <Button onClick={() => setRevisionHistoryModal(null)} variant="outline">Close</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
